export interface RosterChoiceOption {
  /** The roster's `uid` — what `source_team_player_roster_id` expects. */
  id: string
  label: string
  /** Date range, or "Unlinked Roster" when the roster has no season dates. */
  sublabel: string
}

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value)

const formatDay = (value: string): string => {
  const date = new Date(value.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

/**
 * Maps `GET /teams/:id/rosters` into the clone options the add-team roster step
 * offers.
 *
 * Two traps this endpoint carries, both load-bearing:
 * 1. It returns a BARE ARRAY, not the usual `{ data }` envelope.
 * 2. Rosters are keyed by `uid`, not `id` — and `uid` is what the add-team
 *    payload's `source_team_player_roster_id` expects.
 */
export const toRosterChoices = (payload: unknown): RosterChoiceOption[] => {
  const rows = Array.isArray(payload)
    ? (payload as Array<Record<string, unknown>>)
    : []

  return rows.map((row) => {
    const startsAt = str(row.starts_at)
    const endsAt = str(row.ends_at)
    const start = startsAt ? formatDay(startsAt) : ''
    const end = endsAt ? formatDay(endsAt) : ''

    return {
      id: str(row.uid),
      label: str(row.schedule_name) || str(row.name),
      sublabel: start && end ? `${start} - ${end}` : 'Unlinked Roster',
    }
  })
}

/**
 * The roster already tied to this competition, if any.
 *
 * Matched against the ROOT competition id, not the division the team is being
 * added to — a roster spans the whole competition. The legacy Setup tab made
 * exactly this comparison; preserve the asymmetry.
 */
export const findCompetitionRoster = (
  payload: unknown,
  rootScheduleId: string
): RosterChoiceOption | null => {
  const rows = Array.isArray(payload)
    ? (payload as Array<Record<string, unknown>>)
    : []

  const index = rows.findIndex(
    (row) => str(row.schedule_uid) === rootScheduleId
  )

  return index === -1 ? null : toRosterChoices(rows)[index]
}
